/**
 * Feature Flag Types and Enums
 *
 * All types and enums needed for the feature flag system.
 */

/**
 * Available feature flags in the system
 */
export enum FeatureFlag {
  ENABLE_SUBSCRIPTIONS = 'ENABLE_SUBSCRIPTIONS',
}

/**
 * Configuration for a single feature flag
 */
export interface FeatureFlagConfig {
  /** The key name of the feature flag */
  key: string;
  /** Human-readable name */
  name: string;
  /** Description of what this flag controls */
  description: string;
  /** Default value if not set */
  defaultValue: boolean;
  /** Environment variable name to check */
  envVar?: string;
}

/**
 * Object containing all feature flag values
 */
export type FeatureFlagValues = {
  [K in FeatureFlag]: boolean;
};

/**
 * Configuration object for the FeatureFlagManager
 */
export interface FeatureFlagManagerConfig {
  /** Override environment variables for testing */
  envOverrides?: Record<string, string>;
  /** Enable debug logging */
  debug?: boolean;
}

/**
 * Context value provided by FeatureFlagProvider
 */
export interface FeatureFlagContextValue {
  /** Current feature flag values */
  flags: FeatureFlagValues;
  /** Check if a flag is enabled */
  isEnabled: (flag: FeatureFlag) => boolean;
  /** Check if all flags in array are enabled */
  areEnabled: (flags: FeatureFlag[]) => boolean;
  /** Check if any flag in array is enabled */
  isAnyEnabled: (flags: FeatureFlag[]) => boolean;
  /** Get configuration for a flag */
  getConfig: (flag: FeatureFlag) => FeatureFlagConfig;
  /** Whether the provider is initialized */
  isInitialized: boolean;
}

/**
 * Props for FeatureFlagProvider component
 */
export interface FeatureFlagProviderProps {
  children: React.ReactNode;
  /** Override configuration for testing */
  config?: FeatureFlagManagerConfig;
  /** Override specific flag values for testing */
  testValues?: Partial<FeatureFlagValues>;
}

/**
 * Result type for useFeatureFlag hook
 */
export interface UseFeatureFlagResult {
  /** Whether the flag is enabled */
  isEnabled: boolean;
  /** Flag configuration */
  config: FeatureFlagConfig;
  /** Whether the provider is initialized */
  isInitialized: boolean;
}

/**
 * Result type for useFeatureFlags hook
 */
export interface UseFeatureFlagsResult {
  /** All flag values */
  flags: FeatureFlagValues;
  /** Check if a flag is enabled */
  isEnabled: (flag: FeatureFlag) => boolean;
  /** Check if all flags are enabled */
  areEnabled: (flags: FeatureFlag[]) => boolean;
  /** Check if any flag is enabled */
  isAnyEnabled: (flags: FeatureFlag[]) => boolean;
  /** Whether the provider is initialized */
  isInitialized: boolean;
}

/**
 * Options for useSubscriptionFeature hook
 */
export interface UseSubscriptionFeatureOptions {
  /** Fallback value when subscriptions are disabled */
  fallbackValue?: boolean;
  /** Whether to redirect to home when disabled */
  redirectOnDisabled?: boolean;
}

/**
 * Result type for useSubscriptionFeature hook
 */
export interface UseSubscriptionFeatureResult {
  /** Whether subscription features are enabled */
  isEnabled: boolean;
  /** Whether currently loading */
  isLoading: boolean;
  /** Render subscription content conditionally */
  renderSubscriptionContent: (
    content: React.ReactNode,
    fallback?: React.ReactNode
  ) => React.ReactNode;
}
